declare const window: any;

export interface ISavedPassword {
  email: string;
  password: string;
}

export type PasswordSaveResult = 'saved' | 'cancelled' | 'unavailable' | 'failed';

function nativeRequest<T>(action: string, args: string[] = []): Promise<T> {
  return new Promise((resolve, reject) => {
    const cordova = typeof window !== 'undefined' && window.cordova;
    if (!cordova || !['ios', 'android'].includes(cordova.platformId) || typeof cordova.exec !== 'function') {
      reject({code: 'unavailable'});
      return;
    }
    // Never log the bridge arguments/results: they contain the password.
    cordova.exec(resolve, reject, 'VVPasswordManager', action, args);
  });
}

export function isPasswordManagerAvailable(): Promise<boolean> {
  // Android 14+ hides the picker when its silent query confirms no candidates.
  // Native platforms without that query keep it available; no local saved flag.
  return nativeRequest<boolean>('available').catch(() => false);
}

export function getSavedPassword(): Promise<ISavedPassword | undefined> {
  return nativeRequest<ISavedPassword>('get').then(value => {
    if (!value || typeof value.email !== 'string' || !value.email
      || typeof value.password !== 'string' || !value.password) {
      throw {code: 'unavailable'};
    }
    return value;
  }).catch(error => {
    if (error && error.code === 'cancelled') {
      return undefined;
    }
    // Do not propagate native error details into crash reporting.
    throw new Error('Saved passwords are unavailable');
  });
}

// Only call after the server has accepted these credentials. Saving is optional
// and must never turn a successful login/signup into an authentication failure.
export function savePassword(email: string, password: string): Promise<PasswordSaveResult> {
  if (!email || !password) {
    return Promise.resolve('unavailable');
  }
  return nativeRequest<void>('save', [email, password])
    .then((): PasswordSaveResult => 'saved')
    .catch((error): PasswordSaveResult => {
      if (error && (error.code === 'cancelled' || error.code === 'unavailable')) {
        return error.code;
      }
      return 'failed';
    });
}

export function clearPasswordManagerSession(): Promise<void> {
  // Android asks providers to forget active sign-in state on logout; it does
  // not delete saved credentials. iOS has no equivalent password-session API.
  return nativeRequest<void>('clearSession').catch(() => undefined);
}



// WEBPACK FOOTER //
// ./src/helpers/passwordManager.ts